/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * certificateShare
 * ----------------
 * Share a certificate: the person picks PNG or PDF, the file is built in the
 * browser (buildCertificateFile), then:
 *   - a phone browser that can share files (Web Share API level 2) opens the
 *     phone's own share sheet with the file attached; it lists only the apps
 *     that accept that kind of file;
 *   - elsewhere (a desktop browser) the file is saved and the apps that accept
 *     that format are offered with the message and link filled in — a website
 *     cannot attach a file when it opens WhatsApp, Telegram or X.
 * Instagram and X take images only, so a PDF is not offered to them.
 */

export const SHARE_FORMATS = {
  png: { label: 'Image (PNG)', hint: 'Shows as a picture. For WhatsApp, Instagram, Telegram and X.', icon: 'image' },
  pdf: { label: 'Document (PDF)', hint: 'Sent as a file, best for printing. For WhatsApp, Telegram and email.', icon: 'picture_as_pdf' },
};

const enc = encodeURIComponent;

/** The apps a desktop browser can open for each format (message + link). */
export const SHARE_TARGETS = {
  png: ['whatsapp', 'telegram', 'x', 'copy'],
  pdf: ['whatsapp', 'telegram', 'email', 'copy'],
};

export const TARGETS = {
  whatsapp: { label: 'WhatsApp', icon: 'chat', url: ({ text, link }) => `https://wa.me/?text=${enc(`${text} ${link}`)}` },
  telegram: { label: 'Telegram', icon: 'send', url: ({ text, link }) => `https://t.me/share/url?url=${enc(link)}&text=${enc(text)}` },
  x: { label: 'X', icon: 'alternate_email', url: ({ text, link }) => `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(link)}` },
  email: {
    label: 'Email', icon: 'mail',
    url: ({ text, link, title }) => `mailto:?subject=${enc(title)}&body=${enc(`${text}\n\n${link}`)}`,
  },
  copy: { label: 'Copy link', icon: 'link', url: null },
};

/** True when this browser can hand `file` to the device's share sheet. */
export function canShareFile(file) {
  try {
    return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
      && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] });
  } catch (_) {
    return false;
  }
}

/** Opens the device's share sheet. Resolves false when the person closed it. */
export async function shareFile(file, { text, link, title }) {
  try {
    await navigator.share({ files: [file], title, text: `${text} ${link}` });
    return true;
  } catch (err) {
    if (err && err.name === 'AbortError') return false;
    throw err;
  }
}

/** Copies the message and link; false when the browser refused. */
export async function copyShareText({ text, link }) {
  try {
    await navigator.clipboard.writeText(`${text} ${link}`);
    return true;
  } catch (_) {
    return false;
  }
}
